/**
 * Category-specific item detail management.
 *
 * Each non-tool category has a dedicated 1:1 detail table in the DB
 * (e.g. `board_game_details`, `book_details`). This module provides:
 *   - Enum option arrays for dropdown selects in the UI
 *   - `CategoryDetailsState`: a unified in-memory form state for all categories
 *   - `validateCategoryDetails`: pre-save validation
 *   - `saveCategoryDetails`: upserts the correct detail table for a category
 *   - `loadCategoryDetails`: reads from the correct detail table
 *   - `deleteCategoryDetails`: cleans up the detail table when the category changes
 *
 * The dispatch tables (`SAVE_HANDLERS`, `LOAD_HANDLERS`) map each category to
 * its dedicated handler function, keeping the main exported functions thin.
 */
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { ItemCategory } from '@/lib/itemCategoryConfig';

export type BoardGameType = Database['public']['Enums']['board_game_type'];
export type PuzzleType = Database['public']['Enums']['puzzle_type'];
export type PuzzleTheme = Database['public']['Enums']['puzzle_theme'];
export type BookFormat = Database['public']['Enums']['book_format'];
export type BookGenre = Database['public']['Enums']['book_genre'];
export type ToyType = Database['public']['Enums']['toy_type'];
export type EventPartyType = Database['public']['Enums']['event_party_type'];
export type TransportSize = Database['public']['Enums']['transport_size'];
export type PowerType = Database['public']['Enums']['power_type'];

export const BOARD_GAME_TYPE_OPTIONS: { value: BoardGameType; label: string }[] = [
  { value: 'FAMILY', label: 'Family' },
  { value: 'PARTY', label: 'Party' },
  { value: 'STRATEGY', label: 'Strategy' },
  { value: 'COOPERATIVE', label: 'Cooperative' },
  { value: 'CARD', label: 'Card' },
  { value: 'DECK_BUILDING', label: 'Deck Building' },
  { value: 'ENGINE_BUILDING', label: 'Engine Building' },
  { value: 'AREA_CONTROL', label: 'Area Control' },
  { value: 'DEXTERITY', label: 'Dexterity' },
  { value: 'TRIVIA_WORD', label: 'Trivia / Word' },
  { value: 'CHILDRENS', label: "Children's" },
  { value: 'OTHER', label: 'Other' },
];

export const PUZZLE_TYPE_OPTIONS: { value: PuzzleType; label: string }[] = [
  { value: 'JIGSAW', label: 'Jigsaw' },
  { value: '3D', label: '3D' },
  { value: 'WOODEN', label: 'Wooden' },
  { value: 'BRAIN_TEASER', label: 'Brain Teaser' },
  { value: 'OTHER', label: 'Other' },
];

export const PUZZLE_THEME_OPTIONS: { value: PuzzleTheme; label: string }[] = [
  { value: 'LANDSCAPE_SEASCAPE', label: 'Landscape / Seascape' },
  { value: 'ANIMALS_NATURE', label: 'Animals / Nature' },
  { value: 'ART_ILLUSTRATION', label: 'Art / Illustration' },
  { value: 'CITIES_LANDMARKS', label: 'Cities / Landmarks' },
  { value: 'FANTASY_SCI_FI', label: 'Fantasy / Sci-Fi' },
  { value: 'FILM_TV_GAMES', label: 'Film / TV / Games' },
  { value: 'CHILDRENS', label: "Children's" },
  { value: 'SEASONAL_HOLIDAY', label: 'Seasonal / Holiday' },
  { value: 'OTHER', label: 'Other' },
];

export const BOOK_FORMAT_OPTIONS: { value: BookFormat; label: string }[] = [
  { value: 'PAPERBACK', label: 'Paperback' },
  { value: 'HARDBACK', label: 'Hardback' },
  { value: 'AUDIO', label: 'Audio' },
  { value: 'EBOOK', label: 'eBook' },
  { value: 'OTHER', label: 'Other' },
];

export const BOOK_GENRE_OPTIONS: { value: BookGenre; label: string }[] = [
  { value: 'FICTION_GENERAL', label: 'Fiction (General)' },
  { value: 'LITERARY_FICTION', label: 'Literary Fiction' },
  { value: 'MYSTERY_THRILLER', label: 'Mystery / Thriller' },
  { value: 'SCIENCE_FICTION', label: 'Science Fiction' },
  { value: 'FANTASY', label: 'Fantasy' },
  { value: 'ROMANCE', label: 'Romance' },
  { value: 'HISTORICAL_FICTION', label: 'Historical Fiction' },
  { value: 'HORROR', label: 'Horror' },
  { value: 'YOUNG_ADULT', label: 'Young Adult' },
  { value: 'CHILDRENS', label: "Children's" },
  { value: 'NONFICTION_GENERAL', label: 'Non-fiction (General)' },
  { value: 'BIOGRAPHY_MEMOIR', label: 'Biography / Memoir' },
  { value: 'HISTORY', label: 'History' },
  { value: 'BUSINESS_FINANCE', label: 'Business / Finance' },
  { value: 'SELF_HELP', label: 'Self-help' },
  { value: 'SCIENCE_TECH', label: 'Science / Tech' },
  { value: 'HEALTH_FITNESS', label: 'Health / Fitness' },
  { value: 'COOKING_FOOD', label: 'Cooking / Food' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'RELIGION_SPIRITUALITY', label: 'Religion / Spirituality' },
  { value: 'POETRY', label: 'Poetry' },
  { value: 'COMICS_GRAPHIC_NOVEL', label: 'Comics / Graphic Novel' },
  { value: 'REFERENCE_EDUCATION', label: 'Reference / Education' },
  { value: 'OTHER', label: 'Other' },
];

export const TOY_TYPE_OPTIONS: { value: ToyType; label: string }[] = [
  { value: 'BUILDING', label: 'Building' },
  { value: 'ELECTRONIC', label: 'Electronic' },
  { value: 'OUTDOOR', label: 'Outdoor' },
  { value: 'ROLEPLAY', label: 'Roleplay' },
  { value: 'PLUSH', label: 'Plush' },
  { value: 'FIGURE_DOLLS', label: 'Figure / Dolls' },
  { value: 'EDUCATIONAL', label: 'Educational' },
  { value: 'ARTS_CRAFTS', label: 'Arts & Crafts' },
  { value: 'VEHICLES', label: 'Vehicles' },
  { value: 'OTHER', label: 'Other' },
];

export const EVENT_PARTY_TYPE_OPTIONS: { value: EventPartyType; label: string }[] = [
  { value: 'AUDIO', label: 'Audio' },
  { value: 'LIGHTING', label: 'Lighting' },
  { value: 'DECOR', label: 'Decor' },
  { value: 'FURNITURE', label: 'Furniture' },
  { value: 'GAMES_ENTERTAINMENT', label: 'Games / Entertainment' },
  { value: 'BBQ_COOKING', label: 'BBQ / Cooking' },
  { value: 'SHELTER', label: 'Shelter' },
  { value: 'PROJECTOR_SCREEN', label: 'Projector / Screen' },
  { value: 'PHOTO_BOOTH', label: 'Photo Booth' },
  { value: 'OTHER', label: 'Other' },
];

export const TRANSPORT_SIZE_OPTIONS: { value: TransportSize; label: string }[] = [
  { value: 'SMALL_BOOT', label: 'Small Boot' },
  { value: 'LARGE_BOOT', label: 'Large Boot' },
  { value: 'VAN', label: 'Van' },
];

export const POWER_TYPE_OPTIONS: { value: PowerType; label: string }[] = [
  { value: 'MAINS', label: 'Mains' },
  { value: 'BATTERY', label: 'Battery' },
  { value: 'NONE', label: 'None' },
];

/**
 * Unified in-memory form state for all category-specific detail fields.
 * Only the sub-object matching the item's category is populated; the rest
 * hold empty defaults and are ignored during save/validation.
 */
export interface CategoryDetailsState {
  boardGame: {
    publisher: string;
    minPlayers: string;
    maxPlayers: string;
    playTimeMinutes: string;
    recommendedAge: string;
    gameType: BoardGameType | '';
    missingComponents: boolean;
    missingComponentsNotes: string;
  };
  puzzle: {
    puzzleType: PuzzleType | '';
    pieceCount: string;
    recommendedAge: string;
    complete: boolean;
    theme: PuzzleTheme | '';
    hasOriginalBox: boolean;
  };
  book: {
    author: string;
    isbn13: string;
    format: BookFormat | '';
    genre: BookGenre | '';
    language: string;
    seriesName: string;
    seriesNumber: string;
    recommendedAge: string;
  };
  toy: {
    brand: string;
    toyType: ToyType | '';
    containsSmallParts: boolean;
    recommendedAge: string;
    batteryRequired: boolean;
  };
  eventParty: {
    eventType: EventPartyType | '';
    powerRequired: boolean;
    powerType: PowerType | '';
    accessoriesIncluded: string;
    transportSize: TransportSize | '';
  };
}

/**
 * Returns a `CategoryDetailsState` with all fields set to their empty/default
 * values.  Used to initialise form state before the user has entered anything,
 * and as the baseline that `loadCategoryDetails` populates from the DB.
 */
export const createEmptyCategoryDetailsState = (): CategoryDetailsState => ({
  boardGame: {
    publisher: '',
    minPlayers: '',
    maxPlayers: '',
    playTimeMinutes: '',
    recommendedAge: '',
    gameType: '',
    missingComponents: false,
    missingComponentsNotes: '',
  },
  puzzle: {
    puzzleType: '',
    pieceCount: '',
    recommendedAge: '',
    complete: true,
    theme: '',
    hasOriginalBox: false,
  },
  book: {
    author: '',
    isbn13: '',
    format: '',
    genre: '',
    language: '',
    seriesName: '',
    seriesNumber: '',
    recommendedAge: '',
  },
  toy: {
    brand: '',
    toyType: '',
    containsSmallParts: false,
    recommendedAge: '',
    batteryRequired: false,
  },
  eventParty: {
    eventType: '',
    powerRequired: false,
    powerType: 'NONE',
    accessoriesIncluded: '',
    transportSize: '',
  },
});

export const DETAIL_TABLE_BY_CATEGORY: Record<
  Exclude<ItemCategory, 'TOOL'>,
  keyof Database['public']['Tables']
> = {
  BOARD_GAME: 'board_game_details',
  PUZZLE: 'puzzle_details',
  BOOK: 'book_details',
  TOY: 'toy_details',
  EVENT_PARTY: 'event_party_details',
};

// Parses a form string value to an integer. Throws a user-friendly error if
// empty or non-numeric, since these fields are marked required in the UI.
const parseRequiredNumber = (value: string, fieldName: string) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) throw new Error(`${fieldName} is required.`);
  return parsed;
};

// Parses an optional form string value to an integer, or null when blank.
const parseOptionalNumber = (value: string) => {
  if (!value.trim()) return null;
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) throw new Error('Invalid number value.');
  return parsed;
};

// TOOL has no category-specific detail table, so operations on it are no-ops.
const isCategoryWithoutDetails = (
  category: ItemCategory | '' | null | undefined,
): category is '' | 'TOOL' | null | undefined => {
  return !category || category === 'TOOL';
};

/**
 * Returns true when any mandatory board game numeric fields are missing.
 * @param details
 */
const hasMissingBoardGameRequiredValues = (details: CategoryDetailsState['boardGame']) => {
  return !details.minPlayers || !details.maxPlayers || !details.playTimeMinutes;
};

/**
 * Returns true when the mandatory piece count is missing for a puzzle.
 * @param details
 */
const hasMissingPuzzleRequiredValues = (details: CategoryDetailsState['puzzle']) => {
  return !details.pieceCount;
};

/**
 * Validates category-specific detail fields before saving.
 *
 * Returns a user-facing error string when validation fails, or `null` when all
 * required fields are present. TOOL items and items with no category are
 * considered valid immediately (they have no detail table).
 *
 * EVENT_PARTY has bidirectional power coherence rules: `powerRequired` and
 * `powerType` must agree -- if power is required then `powerType` must not be
 * NONE, and vice versa.
 *
 * @param category - The item's category enum value, or empty string for uncategorised items.
 * @param details - The current in-memory form state for all category-specific fields.
 * @returns A user-facing validation error string, or `null` when valid.
 */
export const validateCategoryDetails = (
  category: ItemCategory | '',
  details: CategoryDetailsState,
): string | null => {
  if (isCategoryWithoutDetails(category)) return null;

  if (category === 'BOARD_GAME') {
    if (!details.boardGame.gameType) return 'Please select a board game type.';
    if (hasMissingBoardGameRequiredValues(details.boardGame))
      return 'Please complete all required board game fields.';
  }

  if (category === 'PUZZLE') {
    if (!details.puzzle.puzzleType) return 'Please select a puzzle type.';
    if (!details.puzzle.theme) return 'Please select a puzzle theme.';
    if (hasMissingPuzzleRequiredValues(details.puzzle))
      return 'Please complete all required puzzle fields.';
  }

  if (category === 'BOOK') {
    if (!details.book.author.trim()) return 'Author is required for books.';
    if (!details.book.format || !details.book.genre) return 'Please select book format and genre.';
  }

  if (category === 'TOY') {
    if (!details.toy.toyType) return 'Please select a toy type.';
  }

  if (category === 'EVENT_PARTY') {
    if (!details.eventParty.eventType) return 'Please select an event type.';
    if (!details.eventParty.transportSize) return 'Please select a transport size.';
    if (!details.eventParty.powerType) return 'Please select power type.';
    if (!details.eventParty.powerRequired && details.eventParty.powerType !== 'NONE')
      return 'Power type must be None when power is not required.';
    if (details.eventParty.powerRequired && details.eventParty.powerType === 'NONE')
      return 'Power type cannot be None when power is required.';
  }

  return null;
};

// Each upsert helper converts the camelCase form state to snake_case DB columns
// and calls `.upsert()`, which inserts a new row or updates the existing one
// based on the `item_id` primary key.  `.trim() || null` normalises empty
// string inputs to null so optional text fields are stored cleanly.
const upsertBoardGameDetails = async (
  itemId: string,
  details: CategoryDetailsState['boardGame'],
) => {
  const { error } = await supabase.from('board_game_details').upsert({
    item_id: itemId,
    publisher: details.publisher.trim() || null,
    min_players: parseRequiredNumber(details.minPlayers, 'Minimum players'),
    max_players: parseRequiredNumber(details.maxPlayers, 'Maximum players'),
    play_time_minutes: parseRequiredNumber(details.playTimeMinutes, 'Play time'),
    recommended_age: parseOptionalNumber(details.recommendedAge),
    game_type: details.gameType as BoardGameType,
    missing_components: details.missingComponents,
    missing_components_notes: details.missingComponentsNotes.trim() || null,
  });

  if (error) throw error;
};

const upsertPuzzleDetails = async (itemId: string, details: CategoryDetailsState['puzzle']) => {
  const { error } = await supabase.from('puzzle_details').upsert({
    item_id: itemId,
    puzzle_type: details.puzzleType as PuzzleType,
    piece_count: parseRequiredNumber(details.pieceCount, 'Piece count'),
    recommended_age: parseOptionalNumber(details.recommendedAge),
    complete: details.complete,
    theme: details.theme as PuzzleTheme,
    has_original_box: details.hasOriginalBox,
  });

  if (error) throw error;
};

const upsertBookDetails = async (itemId: string, details: CategoryDetailsState['book']) => {
  const { error } = await supabase.from('book_details').upsert({
    item_id: itemId,
    author: details.author.trim(),
    isbn13: details.isbn13.trim() || null,
    format: details.format as BookFormat,
    genre: details.genre as BookGenre,
    language: details.language.trim() || null,
    series_name: details.seriesName.trim() || null,
    series_number: parseOptionalNumber(details.seriesNumber),
    recommended_age: parseOptionalNumber(details.recommendedAge),
  });

  if (error) throw error;
};

const upsertToyDetails = async (itemId: string, details: CategoryDetailsState['toy']) => {
  const { error } = await supabase.from('toy_details').upsert({
    item_id: itemId,
    brand: details.brand.trim() || null,
    toy_type: details.toyType as ToyType,
    contains_small_parts: details.containsSmallParts,
    recommended_age: parseOptionalNumber(details.recommendedAge),
    battery_required: details.batteryRequired,
  });

  if (error) throw error;
};

const upsertEventPartyDetails = async (
  itemId: string,
  details: CategoryDetailsState['eventParty'],
) => {
  const { error } = await supabase.from('event_party_details').upsert({
    item_id: itemId,
    event_type: details.eventType as EventPartyType,
    power_required: details.powerRequired,
    power_type: details.powerType as PowerType,
    accessories_included: details.accessoriesIncluded.trim() || null,
    transport_size: details.transportSize as TransportSize,
  });

  if (error) throw error;
};

// Dispatch table: maps each category to its dedicated upsert function.
// `saveCategoryDetails` uses this to avoid a long switch/if-else chain.
const SAVE_HANDLERS: Record<
  Exclude<ItemCategory, 'TOOL'>,
  (itemId: string, details: CategoryDetailsState) => Promise<void>
> = {
  BOARD_GAME: (itemId, details) => upsertBoardGameDetails(itemId, details.boardGame),
  PUZZLE: (itemId, details) => upsertPuzzleDetails(itemId, details.puzzle),
  BOOK: (itemId, details) => upsertBookDetails(itemId, details.book),
  TOY: (itemId, details) => upsertToyDetails(itemId, details.toy),
  EVENT_PARTY: (itemId, details) => upsertEventPartyDetails(itemId, details.eventParty),
};

const loadBoardGameDetails = async (itemId: string, state: CategoryDetailsState) => {
  const { data, error } = await supabase
    .from('board_game_details')
    .select('*')
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return state;

  state.boardGame = {
    publisher: data.publisher ?? '',
    minPlayers: String(data.min_players),
    maxPlayers: String(data.max_players),
    playTimeMinutes: String(data.play_time_minutes),
    recommendedAge: data.recommended_age != null ? String(data.recommended_age) : '',
    gameType: data.game_type,
    missingComponents: data.missing_components,
    missingComponentsNotes: data.missing_components_notes ?? '',
  };

  return state;
};

const loadPuzzleDetails = async (itemId: string, state: CategoryDetailsState) => {
  const { data, error } = await supabase
    .from('puzzle_details')
    .select('*')
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return state;

  state.puzzle = {
    puzzleType: data.puzzle_type,
    pieceCount: String(data.piece_count),
    recommendedAge: data.recommended_age != null ? String(data.recommended_age) : '',
    complete: data.complete,
    theme: data.theme,
    hasOriginalBox: Boolean(data.has_original_box),
  };

  return state;
};

const loadBookDetails = async (itemId: string, state: CategoryDetailsState) => {
  const { data, error } = await supabase
    .from('book_details')
    .select('*')
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return state;

  state.book = {
    author: data.author,
    isbn13: data.isbn13 ?? '',
    format: data.format,
    genre: data.genre,
    language: data.language ?? '',
    seriesName: data.series_name ?? '',
    seriesNumber: data.series_number ? String(data.series_number) : '',
    recommendedAge: data.recommended_age != null ? String(data.recommended_age) : '',
  };

  return state;
};

const loadToyDetails = async (itemId: string, state: CategoryDetailsState) => {
  const { data, error } = await supabase
    .from('toy_details')
    .select('*')
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return state;

  state.toy = {
    brand: data.brand ?? '',
    toyType: data.toy_type,
    containsSmallParts: data.contains_small_parts,
    recommendedAge: data.recommended_age != null ? String(data.recommended_age) : '',
    batteryRequired: data.battery_required,
  };

  return state;
};

const loadEventPartyDetails = async (itemId: string, state: CategoryDetailsState) => {
  const { data, error } = await supabase
    .from('event_party_details')
    .select('*')
    .eq('item_id', itemId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return state;

  state.eventParty = {
    eventType: data.event_type,
    powerRequired: data.power_required,
    powerType: data.power_type,
    accessoriesIncluded: data.accessories_included ?? '',
    transportSize: data.transport_size,
  };

  return state;
};

// Dispatch table for loading: mirrors SAVE_HANDLERS, mapping categories to
// their dedicated load function. Each loader uses `maybeSingle()` so it
// returns null (not an error) when no detail row exists yet.
const LOAD_HANDLERS: Record<
  Exclude<ItemCategory, 'TOOL'>,
  (itemId: string, state: CategoryDetailsState) => Promise<CategoryDetailsState>
> = {
  BOARD_GAME: loadBoardGameDetails,
  PUZZLE: loadPuzzleDetails,
  BOOK: loadBookDetails,
  TOY: loadToyDetails,
  EVENT_PARTY: loadEventPartyDetails,
};

/**
 * Deletes the category-specific detail row for an item.
 *
 * Called when the user changes an item's category to prevent stale detail
 * rows from accumulating in the old category's table.
 *
 * @param itemId - The UUID of the item whose detail row should be removed.
 * @param category - The category whose detail table to delete from. No-op for
 *   TOOL, `null`, or `undefined`.
 * @throws Rethrows any Supabase error from the delete operation.
 */
export const deleteCategoryDetails = async (
  itemId: string,
  category: ItemCategory | null | undefined,
) => {
  if (isCategoryWithoutDetails(category)) return;

  const table = DETAIL_TABLE_BY_CATEGORY[category];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from as any)(table).delete().eq('item_id', itemId);
  if (error) throw error;
};

/**
 * Upserts the category-specific detail row for an item.
 *
 * Safe to call on both create and edit -- the underlying DB upserts handle
 * insert-or-update based on `item_id`. No-op for TOOL or empty category.
 *
 * @param itemId - The UUID of the item to save details for.
 * @param category - The item's category enum value, or empty string.
 * @param details - The current in-memory form state for all category-specific fields.
 * @throws Rethrows any Supabase error from the upsert, or an `Error` if a
 *   required numeric field is missing or invalid.
 */
export const saveCategoryDetails = async (
  itemId: string,
  category: ItemCategory | '',
  details: CategoryDetailsState,
) => {
  if (isCategoryWithoutDetails(category)) return;

  const saveForCategory = SAVE_HANDLERS[category];
  return saveForCategory(itemId, details);
};

/**
 * Loads category-specific details for an item into a `CategoryDetailsState`.
 *
 * Returns empty defaults when no detail row exists (new items or TOOL category).
 *
 * @param itemId - The UUID of the item to load details for.
 * @param category - The item's category enum value, or empty string.
 * @returns A fully populated `CategoryDetailsState` with the matching sub-object
 *   hydrated from the database, or all-empty defaults if no row exists.
 * @throws Rethrows any Supabase query error.
 */
export const loadCategoryDetails = async (
  itemId: string,
  category: ItemCategory | '',
): Promise<CategoryDetailsState> => {
  const state = createEmptyCategoryDetailsState();
  if (isCategoryWithoutDetails(category)) return state;

  const loadForCategory = LOAD_HANDLERS[category];
  // Route each category through a dedicated loader to keep each data transform focused.
  return loadForCategory(itemId, state);
};
